import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getProduct, productDetailImageUrl, productMainImageUrl } from "../api/product";
import { addToCart } from "../api/cart";
import { useAuth } from "../context/AuthContext";
import { useCartSummary } from "../context/CartContext";

function formatPrice(v) {
  return Number(v || 0).toLocaleString() + "원";
}

export default function ProductDetailPage() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { auth } = useAuth();
  const { refresh: refreshCart } = useCartSummary();

  const [product, setProduct] = useState(null);
  const [optionId, setOptionId] = useState("");
  const [qty, setQty] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  useEffect(() => {
    setLoading(true);
    setError("");
    getProduct(productId)
      .then((res) => {
        if (!res.success) {
          setError(res.message || "상품을 찾을 수 없습니다.");
          return;
        }
        setProduct(res.data);
        const firstAvailable = (res.data.options || []).find((o) => !o.soldOut);
        setOptionId(firstAvailable ? firstAvailable.optionId : "");
      })
      .catch((err) => setError(err.response?.data?.message || "상품을 불러오지 못했습니다."))
      .finally(() => setLoading(false));
  }, [productId]);

  if (loading) return <div className="page"><p className="text-sub">불러오는 중...</p></div>;
  if (error) return <div className="page"><p className="error">{error}</p></div>;
  if (!product) return null;

  const selectedOption = (product.options || []).find((o) => String(o.optionId) === String(optionId));
  const unitPrice = product.discountedPrice + (selectedOption ? selectedOption.additionalPrice : 0);

  const handleAddToCart = async () => {
    if (!optionId) {
      setError("옵션을 선택해주세요.");
      return;
    }
    setError("");
    try {
      await addToCart(product.productId, Number(optionId), qty, auth?.accessToken);
      refreshCart();
      setNotice("장바구니에 담았습니다.");
      setTimeout(() => setNotice(""), 2500);
    } catch (err) {
      setError(err.response?.data?.message || "장바구니 담기에 실패했습니다.");
    }
  };

  const handleBuyNow = async () => {
    await handleAddToCart();
    navigate("/cart");
  };

  return (
    <div className="page product-detail-page">
      <button type="button" className="link-btn back-link" onClick={() => navigate(-1)}>← 목록으로</button>

      <div className="product-detail-main">
        <img className="product-detail-image" src={productMainImageUrl(product.productId)} alt={product.productName} />
        <div className="product-detail-info">
          <h1 className="page-title">{product.productName}</h1>
          <div className="product-price product-price-lg">
            {product.discountActive ? (
              <>
                <span className="price-original">{formatPrice(product.basePrice)}</span>
                <span className="price-final">{formatPrice(product.discountedPrice)}</span>
              </>
            ) : (
              <span className="price-final">{formatPrice(product.basePrice)}</span>
            )}
          </div>

          <label>옵션</label>
          <select value={optionId} onChange={(e) => setOptionId(e.target.value)}>
            {(product.options || []).map((o) => (
              <option key={o.optionId} value={o.optionId} disabled={o.soldOut}>
                {o.optionName}
                {o.additionalPrice ? ` (+${formatPrice(o.additionalPrice)})` : ""}
                {o.soldOut ? " - 품절" : ""}
              </option>
            ))}
          </select>

          <label>수량</label>
          <div className="qty-stepper">
            <button type="button" onClick={() => setQty((q) => Math.max(1, q - 1))}>-</button>
            <span>{qty}</span>
            <button type="button" onClick={() => setQty((q) => q + 1)}>+</button>
          </div>

          <div className="product-total-price">합계 {formatPrice(unitPrice * qty)}</div>

          {error && <p className="error">{error}</p>}
          {notice && <p className="success">{notice}</p>}

          <div className="product-actions">
            <button type="button" className="btn-outline" onClick={handleAddToCart} disabled={!selectedOption}>장바구니 담기</button>
            <button type="button" onClick={handleBuyNow} disabled={!selectedOption}>바로 구매</button>
          </div>

          {product.precautions && (
            <div className="product-precautions">
              <h3>유의사항</h3>
              {/* 관리자 등록화면(RichTextEditor, CKEditor)에서 HTML로 입력되므로 그대로 렌더링해야
                  태그가 문자 그대로 노출되지 않는다. */}
              <div dangerouslySetInnerHTML={{ __html: product.precautions }} />
            </div>
          )}
        </div>
      </div>

      {product.detailContent && (
        <div className="product-detail-content" dangerouslySetInnerHTML={{ __html: product.detailContent }} />
      )}

      {(product.detailImages || []).length > 0 && (
        <div className="product-detail-images">
          {product.detailImages.map((img) => (
            <img key={img.detailImageId} src={productDetailImageUrl(img.detailImageId)} alt="" />
          ))}
        </div>
      )}
    </div>
  );
}
